import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLoaderData } from 'react-router-dom';
import SummaryVotesGraph from '../../components/SummaryVotesGraph';
import { useTranslation } from 'react-i18next';
import DataCards from '../../components/dataCards/DataCards/DataCards';
import { VotingDataCard } from '../../components/dataCards/dataCardsTemplates/VotingDataCard';
import { getApiClient } from '../../api';
import type { GetVotesByMunicipalityIdAndBodyAndCouncilIdParams, VotesQuery } from '../../api';
import VotingDataCardsFilter from '../../components/dataCards/dataCardsFiltering/VotingDataCardsFilter';
import FilterWrapper from '../../components/dataCards/dataCardsFiltering/FilterWrapper';
import ContentSection from '../../components/common/ContentSection/ContentSection';
import StatChip from '../../components/common/StatChip/StatChip';
import { URLKeys } from '../../Constants';
import type { VoteBasicData } from '../../api';
import { VotingCardDataType } from '../../types';
import { useFormatters } from '../../utils/texts';
import { useAppContext } from '../../context/AppContext';
import { useMunicipalityId } from '../../hooks/useCouncilPath';

function Votings() {
    const { t, i18n } = useTranslation('votings');
    const { bodyType, zastupitelstvoId } = useParams();
    const municipalitaId = useMunicipalityId();
    const urlSearchParams = new URLSearchParams(window.location.search);
    const [data, setData] = useState<VotingCardDataType[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [sortFunction, setSortFunction] = useState<(a: any, b: any) => number>(() => 0);
    const votingSummaryData = useLoaderData() as VoteBasicData;
    const { pocet_hlasovani, pocet_prijatych, pocet_neprijatych, pocet_neplatnych } = votingSummaryData;
    const { basicData } = useAppContext();
    const currentLocale = i18n.language

    const formatter = useFormatters(currentLocale);

    const getData = async () => {
        setLoading(true);
        try {
            const searchParamObject = Object.fromEntries(urlSearchParams.entries()) as Record<string, string>;
            const api = getApiClient();
            const votesData = await api.getVotesByMunicipalityIdAndBodyAndCouncilId(
                municipalitaId ?? '',
                bodyType ?? '',
                parseInt(zastupitelstvoId ?? '0', 10),
                Object.keys(searchParamObject).length ? (searchParamObject as GetVotesByMunicipalityIdAndBodyAndCouncilIdParams) : undefined
            );
            setData(
                (votesData ?? []).map((item: VotesQuery) => ({
                    voteId: item.id_hlasovani,
                    voteNumber: item.cislo_hlasovani,
                    meetingNumber: item.cislo_zasedani ?? '',
                    voteDescription: item.predmet_hlasovani,
                    voteResult: item.vysledek,
                    secret: item.tajne === 1,
                    valid: item.validni === 1,
                    procedural: item.proceduralni === 1
                }))
            );
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };
    const getFilteredSearchParams = () => {
        const filteredParams = new URLSearchParams(urlSearchParams);
        filteredParams.delete(URLKeys.page);
        filteredParams.delete(URLKeys.rows);
        filteredParams.delete(URLKeys.sortBy);
        return filteredParams.toString();
    };

    useEffect(() => {
        getData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getFilteredSearchParams()]);

    if (data !== undefined) {
        return (
            <div className='page-dashboard'>
                <div className='page-header-row'>
                    <ContentSection
                        title={t('votings')}
                        level={1}
                        subtitle={basicData.funkcni_obdobi}
                        className='page-title-section'
                    />
                    <div className='stat-chip-row'>
                        <StatChip
                            icon='stacks'
                            value={formatter.number.format(pocet_hlasovani)}
                            label={t('votingsTotal')}
                        />
                        <StatChip
                            icon='check_box'
                            value={formatter.number.format(pocet_prijatych)}
                            label={t('approvedProposals')}
                        />
                        <StatChip
                            icon='disabled_by_default'
                            value={formatter.number.format(pocet_neprijatych)}
                            label={t('rejectedProposals')}
                        />
                        <StatChip
                            icon='indeterminate_check_box'
                            value={formatter.number.format(pocet_neplatnych)}
                            label={t('invalidVotings')}
                        />
                    </div>
                </div>
                <div className='page-description'>{t('votingsOverview')}</div>
                <ContentSection level={3} title={t('partiesVotings')} subtitle={t('partiesVotingsTooltip')} className='dashboard-card'>
                    <SummaryVotesGraph />
                </ContentSection>
                <ContentSection level={3} title={t('individualVotings')} subtitle={t('individualVotingsTooltip')} className='dashboard-card'>
                    <FilterWrapper filterComponent={<VotingDataCardsFilter setFilterFunc={setSortFunction} />} />
                    <DataCards
                        id='voting-cards'
                        renderCardCallback={(cardData: VotingCardDataType) => <VotingDataCard data={cardData} />}
                        getCardUrl={(item) => `${item.voteId}`}
                        getUniqueId={(item) => item.voteId}
                        cardsData={(() => {
                            if (sortFunction) {
                                return data.sort(sortFunction);
                            }
                            return data;
                        })()}
                        loading={loading}
                    />
                </ContentSection>
            </div>
        );
    }
}

export default Votings;
